import { useMemo } from 'react';
import { core_v1_DataSource } from '@antimatterhq/asl-api-ts-v1';
import { Fieldset } from '@antimatterhq/ui/components/fieldset';
import { Badge } from '@antimatterhq/ui/components/badge';
import Spacer from '@antimatterhq/ui/components/spacer';
import { FieldSpecReview } from '../fieldSpec/FieldSpecReview';
import { CustomNotebookFunctionReview } from '../customNotebookFunction/CustomNotebookFunctionReview';
import { FiltersReview } from '../filters/FiltersReview';
import { JsonExtractReview } from '../jsonExtract/JsonExtractReview';
import { ColumnClusteringReview } from '../columnClustering/ColumnClusteringReview';
import { UnreferencedColumnsReview } from '../preserveUnreferencedColumns/UnreferencedColumnsReview';
import { getFieldTableRows, getFieldSchemaValues } from '@/lib/datasource';
import { filterNulls } from '@/utils/filterNulls';
import { useGetDatasourcePreset } from '@/hooks/content/useGetDatasourcePreset';
import { DatasourceUpgradeDiff } from '@/lib/datasource/upgrade/datasourceUpgrade.types';

export type SilverReviewProps = {
  dataSource: core_v1_DataSource;
  datasourceUpgrade?: DatasourceUpgradeDiff;
  presetVersion?: number;
};

export const SilverReview = ({
  dataSource,
  datasourceUpgrade,
  presetVersion,
}: SilverReviewProps) => {
  const { data: presets } = useGetDatasourcePreset({
    name: dataSource.spec.usePreset || undefined,
    version: presetVersion || dataSource.spec.usePresetVersion,
  });

  const addTables = useMemo(
    () =>
      dataSource.spec.silver?.transform?.presetOverrides?.addTables?.map(
        ({ fields, ...values }) => ({
          ...values,
          fields: getFieldTableRows(
            undefined,
            getFieldSchemaValues(filterNulls(fields)),
            undefined,
          ),
          temporaryFields: getFieldTableRows(
            undefined,
            getFieldSchemaValues(filterNulls(values.utils?.temporaryFields)),
            undefined,
          ),
        }),
      ),
    [dataSource.spec.silver?.transform?.presetOverrides?.addTables],
  );

  const modifyTables = useMemo(
    () =>
      presets?.silver?.transform?.map((preset) => {
        const { addFields, omitFields, name, ...modifyTable } =
          dataSource.spec.silver?.transform?.presetOverrides?.modifyTables?.find(
            (q) => q.name === preset.name,
          ) || {};
        const fields = getFieldTableRows(
          filterNulls(preset.fields),
          getFieldSchemaValues(filterNulls(addFields)),
          filterNulls(omitFields),
        );
        const temporaryFields = getFieldTableRows(
          undefined,
          getFieldSchemaValues(filterNulls(modifyTable.utils?.temporaryFields)),
          undefined,
        );

        return {
          ...modifyTable,
          name: name || '',
          fields,
          temporaryFields,
        };
      }),
    [presets, dataSource.spec.silver?.transform?.presetOverrides?.modifyTables],
  );

  const omitTables =
    dataSource.spec.silver?.transform?.presetOverrides?.omitTables || [];

  if (
    (!modifyTables || modifyTables?.length === 0) &&
    (!addTables || addTables?.length === 0)
  ) {
    return (
      <div>
        <div>
          <Fieldset.Field>
            <Fieldset.Label>No silver tables</Fieldset.Label>
          </Fieldset.Field>
        </div>
      </div>
    );
  }

  return (
    <div>
      {modifyTables?.map((modifyTable) => {
        const upgradeTransform = datasourceUpgrade?.spec.silver.transform.find(
          (q) => q.name === modifyTable.name,
        );

        return (
          <div key={modifyTable.name}>
            <Badge
              variant="filled"
              color="silver"
              data-testid={`review-silver-table-${modifyTable.name}-badge`}
            >
              {modifyTable.name}
            </Badge>

            <Spacer size="lg" />

            {omitTables.includes(modifyTable.name) && (
              <div key={`omit-${modifyTable.name}`}>
                <Fieldset.Field>
                  <Fieldset.Label>Disabled</Fieldset.Label>
                </Fieldset.Field>
              </div>
            )}

            {modifyTable.custom?.function && (
              <CustomNotebookFunctionReview custom={modifyTable.custom} />
            )}

            {!omitTables.includes(modifyTable.name) &&
              !modifyTable.custom?.function && (
                <>
                  <div>
                    <Fieldset.Field>
                      <Fieldset.Label>Field Summary:</Fieldset.Label>
                    </Fieldset.Field>
                    <Fieldset.List>
                      <FieldSpecReview
                        fields={modifyTable.fields}
                        fieldUpgradeStates={upgradeTransform?.fields}
                      />
                    </Fieldset.List>
                  </div>

                  <Spacer size="lg" />

                  <ColumnClusteringReview
                    overrideLiquidColumns={modifyTable.overrideLiquidColumns}
                  />

                  <Spacer size="lg" />

                  <FiltersReview
                    filter={modifyTable.filter || ''}
                    postFilter={modifyTable.postFilter || ''}
                    filterStatus={upgradeTransform?.filter?.status}
                    postFilterStatus={upgradeTransform?.postFilter?.status}
                  />

                  <Spacer size="lg" />

                  <UnreferencedColumnsReview
                    unreferencedColumns={modifyTable.utils?.unreferencedColumns}
                    upgradeState={upgradeTransform?.utils}
                  />

                  <Spacer size="lg" />

                  <JsonExtractReview
                    jsonExtract={modifyTable.utils?.jsonExtract}
                  />

                  <Spacer size="lg" />

                  <div>
                    <Fieldset.Field>
                      <Fieldset.Label>Temporary Fields:</Fieldset.Label>
                    </Fieldset.Field>
                    <Fieldset.List>
                      <FieldSpecReview
                        fields={modifyTable.temporaryFields}
                        fieldUpgradeStates={
                          upgradeTransform?.utils?.temporaryFields
                        }
                      />
                    </Fieldset.List>
                  </div>

                  <Spacer size="2xl" />
                </>
              )}
          </div>
        );
      })}

      {addTables?.map((addTable) => (
        <div key={`add-${addTable.name}`}>
          <Badge
            variant="filled"
            color="silver"
            data-testid={`review-silver-table-${addTable.name}-badge`}
          >
            {addTable.name}
          </Badge>

          <Spacer size="lg" />

          {addTable.custom?.function && (
            <CustomNotebookFunctionReview custom={addTable.custom} />
          )}

          {!addTable.custom?.function && (
            <>
              <div>
                <Fieldset.Field>
                  <Fieldset.Label>Field Summary:</Fieldset.Label>
                </Fieldset.Field>
                <Fieldset.List>
                  <FieldSpecReview fields={addTable.fields} />
                </Fieldset.List>
              </div>

              <Spacer size="lg" />

              <ColumnClusteringReview
                overrideLiquidColumns={addTable.overrideLiquidColumns}
              />

              <Spacer size="lg" />

              <FiltersReview
                filter={addTable.filter || ''}
                postFilter={addTable.postFilter || ''}
              />

              <Spacer size="lg" />

              <UnreferencedColumnsReview
                unreferencedColumns={addTable.utils?.unreferencedColumns}
              />

              <Spacer size="lg" />

              <JsonExtractReview jsonExtract={addTable.utils?.jsonExtract} />

              <Spacer size="lg" />

              <div>
                <Fieldset.Field>
                  <Fieldset.Label>Temporary Fields:</Fieldset.Label>
                </Fieldset.Field>
                <Fieldset.List>
                  <FieldSpecReview fields={addTable.temporaryFields} />
                </Fieldset.List>
              </div>

              <Spacer size="2xl" />
            </>
          )}
        </div>
      ))}
    </div>
  );
};
